import { useState, useRef } from "react";
import { setUnlocked } from "./apiConfig";

const ACCESS_CODE = process.env.REACT_APP_ACCESS_CODE || "1212";

/**
 * Full-screen security gate.
 * Props:
 *   onUnlock() — called when the correct code is entered.
 */
export default function GateScreen({ onUnlock }) {
  const [digits, setDigits] = useState(["", "", "", ""]);
  const [error, setError] = useState(false);

  const inputRefs = [useRef(null), useRef(null), useRef(null), useRef(null)];

  const handleDigitChange = (i, e) => {
    const val = e.target.value.replace(/\D/g, "").slice(-1);
    const next = [...digits];
    next[i] = val;
    setDigits(next);
    if (error) setError(false);
    if (val && i < 3) inputRefs[i + 1].current.focus();
    if (val && i === 3) {
      const full = next.join("");
      if (full === ACCESS_CODE) { setUnlocked(); onUnlock(); }
      else { setError(true); setDigits(["", "", "", ""]); inputRefs[0].current.focus(); }
    }
  };

  const handleDigitKeyDown = (i, e) => {
    if (e.key === "Backspace") {
      if (digits[i]) {
        const next = [...digits]; next[i] = ""; setDigits(next);
      } else if (i > 0) {
        inputRefs[i - 1].current.focus();
      }
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 4);
    const next = ["", "", "", ""];
    pasted.split("").forEach((ch, i) => { next[i] = ch; });
    setDigits(next);
    if (error) setError(false);
    const focusIdx = Math.min(pasted.length, 3);
    inputRefs[focusIdx].current.focus();
    if (pasted.length === 4) {
      if (pasted === ACCESS_CODE) { setUnlocked(); onUnlock(); }
      else { setError(true); setDigits(["", "", "", ""]); inputRefs[0].current.focus(); }
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const full = digits.join("");
    if (full === ACCESS_CODE) { setUnlocked(); onUnlock(); }
    else { setError(true); setDigits(["", "", "", ""]); inputRefs[0].current.focus(); }
  };

  const code = digits.join("");

  return (
    <main className="app-gate">
      <div className="gd-card gd-stack app-gate__card">
        <span className="app-gate__mark" aria-hidden="true">
          <svg className="gd-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <rect width="18" height="11" x="3" y="11" rx="2" ry="2" />
            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
          </svg>
        </span>
        <div className="gd-stack gd-stack--tight">
          <h1 className="title app-gate__title">Figma Reference Finder</h1>
          <p className="small gd-muted app-gate__lead">Internal design tool — enter your access code</p>
        </div>

        <form onSubmit={handleSubmit} noValidate className="gd-stack">
          <div className="gd-field">
            <div className="gd-label" id="gate-code-label">Access code</div>
            <div
              className="title app-gate__digits"
              role="group"
              aria-labelledby="gate-code-label"
              onPaste={handlePaste}
            >
              {digits.map((d, i) => (
                <input
                  key={i}
                  ref={inputRefs[i]}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={d}
                  autoFocus={i === 0}
                  autoComplete="off"
                  aria-label={`Digit ${i + 1} of 4`}
                  aria-invalid={error}
                  aria-describedby={error ? "gate-code-error" : undefined}
                  className="gd-input app-gate__digit"
                  onChange={(e) => handleDigitChange(i, e)}
                  onKeyDown={(e) => handleDigitKeyDown(i, e)}
                />
              ))}
            </div>
            {error && (
              <span id="gate-code-error" role="alert" className="gd-error">
                Incorrect code — try again
              </span>
            )}
          </div>

          <button type="submit" disabled={code.length < 4} className="gd-btn gd-btn--primary gd-btn--block">
            Unlock
          </button>
        </form>
      </div>
    </main>
  );
}
